// GPU server page — power control, live status and the embedded dashboard.
//
// Polling is deliberately restrained: one timer, never overlapping, the
// interval comes from the server (fast while booting, slow while idle), and
// it stops entirely while the tab is in the background.

(function ($) {
  'use strict';

  if ($('#gpu-page').length === 0) return;

  var BTN_DANGER  = 'bg-red-500 hover:bg-red-600 text-white';
  var BTN_SUCCESS = 'bg-green-600 hover:bg-green-700 text-white';

  var HISTORY_PAGE_SIZE = 20;

  var dashboardUrl = $('#gpu-page').data('dashboard-url') || '';
  var pollTimer = null;
  var busy = false;
  var framedUrl = null;

  // Where we are in the log. The server owns the total; we only ever ask for
  // one page, so these two are the whole of the pager's state.
  var historyOffset = 0;
  var historyTotal = 0;

  var STATES = {
    online: {
      label: 'Online',
      hint: 'Dashboard is reachable',
      wrap: 'bg-green-100 dark:bg-green-900/30',
      icon: 'bi-check-circle-fill text-green-600 dark:text-green-400',
    },
    starting: {
      label: 'Starting',
      hint: 'Powered on, waiting for the dashboard to answer',
      wrap: 'bg-amber-100 dark:bg-amber-900/30',
      icon: 'bi-arrow-repeat text-amber-600 dark:text-amber-400',
    },
    stopping: {
      label: 'Stopping',
      hint: 'Shutting down — this takes about a minute',
      wrap: 'bg-amber-100 dark:bg-amber-900/30',
      icon: 'bi-arrow-repeat text-amber-600 dark:text-amber-400',
    },
    offline: {
      label: 'Offline',
      hint: 'Powered off — no GPU charges running',
      wrap: 'bg-slate-100 dark:bg-slate-700',
      icon: 'bi-power text-slate-400',
    },
    error: {
      label: 'Error',
      hint: 'UpCloud reports the server is in an error state',
      wrap: 'bg-red-100 dark:bg-red-900/30',
      icon: 'bi-exclamation-triangle-fill text-red-600 dark:text-red-400',
    },
    unknown: {
      label: 'Unknown',
      hint: 'Could not determine the power state',
      wrap: 'bg-slate-100 dark:bg-slate-700',
      icon: 'bi-question-circle text-slate-400',
    },
  };

  // ---------- Status ----------
  function loadStatus(force) {
    clearTimeout(pollTimer);
    $.get('/api/gpu/status/' + (force ? '?force=1' : ''))
      .done(function (resp) {
        if (resp.ok) {
          paint(resp.gpu);
          schedule(resp.gpu.poll_after);
        } else {
          paintError(resp.error);
          schedule(30);
        }
      })
      .fail(function (xhr) {
        paintError((xhr.responseJSON && xhr.responseJSON.error) || 'Could not read the status');
        schedule(30);
      });
  }

  function schedule(seconds) {
    clearTimeout(pollTimer);
    if (document.hidden) return;
    pollTimer = setTimeout(loadStatus, Math.max(5, seconds || 20) * 1000);
  }

  function paint(gpu) {
    var s = STATES[gpu.state] || STATES.unknown;

    $('#gpu-state-icon-wrap').attr(
      'class', 'w-14 h-14 rounded-full flex items-center justify-center ' + s.wrap);
    $('#gpu-state-icon').attr('class', 'bi text-2xl ' + s.icon);
    $('#gpu-state-label').text(s.label);
    $('#gpu-state-hint').text(
      gpu.state === 'starting' && !gpu.host_up ? 'Powered on, still booting' : s.hint);
    $('#gpu-checked').text('checked ' + relative(gpu.checked_ago) + ' ago');

    paintWho('#gpu-last-on', '#gpu-last-on-when', gpu.last_on);
    paintWho('#gpu-last-off', '#gpu-last-off-when', gpu.last_off);

    $('#gpu-uptime-today').text((gpu.uptime_today && gpu.uptime_today.human) || '0m');
    $('#gpu-session').text(gpu.session_human || '—');

    // Start only when it is really off; stop only when it is really up.
    $('#gpu-start').prop('disabled', busy || gpu.power_state !== 'stopped');
    $('#gpu-stop').prop('disabled', busy || gpu.power_state !== 'started');

    paintDashboard(gpu);
  }

  function paintWho(whoSel, whenSel, entry) {
    if (!entry) {
      $(whoSel).text('—');
      $(whenSel).text('');
      return;
    }
    $(whoSel).text(entry.who);
    $(whenSel).text(formatTime(entry.created_at) + ' · ' + relative(since(entry.created_at)) + ' ago');
  }

  function paintError(message) {
    var s = STATES.error;
    $('#gpu-state-icon-wrap').attr(
      'class', 'w-14 h-14 rounded-full flex items-center justify-center ' + s.wrap);
    $('#gpu-state-icon').attr('class', 'bi text-2xl ' + s.icon);
    $('#gpu-state-label').text('Cannot read status');
    $('#gpu-state-hint').text(message || '');
    $('#gpu-start, #gpu-stop').prop('disabled', true);
  }

  // ---------- Dashboard embed ----------
  function paintDashboard(gpu) {
    var url = gpu.dashboard_url || dashboardUrl;
    var online = gpu.state === 'online';
    // An http dashboard cannot be framed inside an https page.
    var mixed = window.location.protocol === 'https:' && url.indexOf('http://') === 0;

    if (online && !mixed) {
      if (framedUrl !== url) {
        $('#gpu-dashboard-frame').attr('src', url);
        framedUrl = url;
      }
      $('#gpu-dashboard-placeholder').addClass('hidden');
      $('#gpu-dashboard-frame-wrap').removeClass('hidden');
      $('#gpu-dashboard-link').attr('href', url).removeClass('hidden');
      return;
    }

    // Drop the frame so it stops hammering a host that is going away.
    if (framedUrl !== null) {
      $('#gpu-dashboard-frame').attr('src', 'about:blank');
      framedUrl = null;
    }
    $('#gpu-dashboard-frame-wrap').addClass('hidden');
    $('#gpu-dashboard-placeholder').removeClass('hidden');
    $('#gpu-dashboard-link').toggleClass('hidden', !online).attr('href', url);
    $('#gpu-placeholder-link').toggleClass('hidden', !online).attr('href', url);

    if (online && mixed) {
      $('#gpu-placeholder-icon').attr('class', 'bi bi-shield-lock text-3xl text-slate-400');
      $('#gpu-placeholder-title').text('Dashboard is up');
      $('#gpu-placeholder-text').text(
        'This page is served over HTTPS, so the browser blocks the plain HTTP ' +
        'dashboard from being embedded. Open it in a new tab instead.');
    } else if (gpu.state === 'starting') {
      $('#gpu-placeholder-icon').attr('class', 'bi bi-arrow-repeat text-3xl text-amber-500');
      $('#gpu-placeholder-title').text('Server is booting');
      $('#gpu-placeholder-text').text('The dashboard loads by itself once the server answers.');
    } else if (gpu.state === 'stopping') {
      $('#gpu-placeholder-icon').attr('class', 'bi bi-arrow-repeat text-3xl text-amber-500');
      $('#gpu-placeholder-title').text('Server is shutting down');
      $('#gpu-placeholder-text').text('Give it about a minute to power off.');
    } else {
      $('#gpu-placeholder-icon').attr('class', 'bi bi-gpu-card text-3xl text-slate-400');
      $('#gpu-placeholder-title').text('Server is offline');
      $('#gpu-placeholder-text').text('Turn the GPU server on to load the dashboard.');
    }
  }

  // ---------- Power ----------
  function confirmPower(action) {
    var isStart = action === 'start';
    var body = isStart
      ? '<p class="text-slate-600 dark:text-slate-300">Billing for the GPU starts as soon as ' +
        'it boots. Your name is recorded here and posted to the Teams channel.</p>'
      : '<p class="text-slate-600 dark:text-slate-300">Anyone still working on the server will ' +
        'lose their session. The Teams channel gets the run time and today\'s total.</p>';

    var dlg = new Dialog(isStart ? 'Turn ON the GPU server?' : 'Turn OFF the GPU server?',
                         body, { size: 'sm' });
    dlg.setButtons([
      { name: 'Cancel', dismiss: true },
      {
        name: isStart ? 'Turn ON' : 'Turn OFF',
        class: isStart ? BTN_SUCCESS : BTN_DANGER,
        onClick: function () {
          var lb = new LoadingButton(this);
          lb.start();
          power(action, dlg, lb, isStart ? 'Turn ON' : 'Turn OFF');
        },
      },
    ]).show();
  }

  function power(action, dlg, lb, label) {
    busy = true;
    $('#gpu-start, #gpu-stop').prop('disabled', true);

    $.post('/api/gpu/' + action + '/')
      .done(function (resp) {
        busy = false;
        if (resp.ok) {
          dlg.hide();
          Toast.show(resp.message, 'success');
          if (resp.gpu) { paint(resp.gpu); schedule(resp.gpu.poll_after); }
          loadHistory(0);
          loadUptime();
        } else {
          Toast.show(resp.error || 'Power action failed', 'error');
          lb.stop(label);
          loadStatus(true);
        }
      })
      .fail(function (xhr) {
        busy = false;
        Toast.show((xhr.responseJSON && xhr.responseJSON.error) || 'Power action failed', 'error');
        lb.stop(label);
        loadStatus(true);
      });
  }

  // ---------- History ----------
  function loadHistory(offset) {
    var want = Math.max(0, offset || 0);

    $.get('/api/gpu/history/', { limit: HISTORY_PAGE_SIZE, offset: want })
      .done(function (resp) {
        if (!resp.ok) return;
        // Trust the server's echo, not what we asked for — it clamps.
        historyOffset = resp.offset || 0;
        historyTotal = resp.total || 0;
        renderHistory(resp.history);
        renderPager();
        if (resp.uptime_today) {
          $('#gpu-history-meta').text(
            'ON today (' + resp.uptime_today.date + '): ' + resp.uptime_today.human);
        }
      })
      .fail(function () {
        $('#gpu-history-tbody').html(
          '<tr><td colspan="4" class="px-5 py-8 text-center text-slate-500">' +
          'Could not load the history.</td></tr>');
        $('#gpu-history-pager').addClass('hidden');
      });
  }

  function renderPager() {
    var $pager = $('#gpu-history-pager');

    // One page of rows needs no controls at all.
    if (historyTotal <= HISTORY_PAGE_SIZE) {
      $pager.addClass('hidden');
      return;
    }

    var first = historyOffset + 1;
    var last = Math.min(historyOffset + HISTORY_PAGE_SIZE, historyTotal);

    $('#gpu-history-range').text(
      'Showing ' + first + '–' + last + ' of ' + historyTotal);
    $('#gpu-history-prev').prop('disabled', historyOffset <= 0);
    $('#gpu-history-next').prop('disabled', last >= historyTotal);
    $pager.removeClass('hidden').addClass('flex');
  }

  function renderHistory(history) {
    var $tbody = $('#gpu-history-tbody');
    if (!history || history.length === 0) {
      $tbody.html('<tr><td colspan="4" class="px-5 py-8 text-center text-slate-500">' +
                  'No power actions recorded yet.</td></tr>');
      return;
    }

    var html = '';
    history.forEach(function (e) {
      var badge = e.action === 'start'
        ? '<span class="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 ' +
          'dark:bg-green-900/40 dark:text-green-300">ON</span>'
        : '<span class="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 ' +
          'dark:bg-red-900/40 dark:text-red-300">OFF</span>';
      var result = e.ok
        ? '<span class="text-green-600 dark:text-green-400"><i class="bi bi-check-circle mr-1"></i>Done</span>'
        : '<span class="text-red-600 dark:text-red-400" title="' + escapeAttr(e.error) +
          '"><i class="bi bi-x-circle mr-1"></i>Failed</span>';

      html += '<tr>' +
        '<td class="px-5 py-3">' + badge + '</td>' +
        '<td class="px-5 py-3 font-medium">' + escapeHtml(e.who) + '</td>' +
        '<td class="px-5 py-3 text-slate-500 dark:text-slate-400">' +
          escapeHtml(formatTime(e.created_at)) + '</td>' +
        '<td class="px-5 py-3">' + result + '</td>' +
      '</tr>';
    });
    $tbody.html(html);
  }

  // ---------- Monthly uptime ----------
  function loadUptime() {
    $.get('/api/gpu/uptime/')
      .done(function (resp) {
        if (!resp.ok) return;
        renderUptime(resp.months, resp.total);
      })
      .fail(function () {
        $('#gpu-uptime-months').html(
          '<p class="text-sm text-slate-500 dark:text-slate-400">' +
          'Could not load the monthly totals.</p>');
      });
  }

  function renderUptime(months, total) {
    var $box = $('#gpu-uptime-months');

    if (!months || months.length === 0) {
      $box.html('<p class="text-sm text-slate-500 dark:text-slate-400">' +
                'Nothing recorded yet.</p>');
      $('#gpu-uptime-total').text('');
      return;
    }

    $('#gpu-uptime-total').text(total ? 'all time ' + total.human : '');

    // Bars are relative to the busiest month, so the shape of the year is
    // readable even when every month is small.
    var busiest = 0;
    months.forEach(function (m) { busiest = Math.max(busiest, m.seconds || 0); });

    var html = '';
    months.forEach(function (m) {
      var width = busiest > 0 ? Math.round(((m.seconds || 0) / busiest) * 100) : 0;
      var bar = m.is_current ? 'bg-orange-400' : 'bg-orange-500';
      var tag = m.is_current
        ? ' <span class="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ' +
          'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">' +
          'this month</span>'
        : '';

      html += '<div>' +
        '<div class="flex items-baseline justify-between gap-2">' +
          '<span class="text-sm font-medium">' + escapeHtml(m.label) + tag + '</span>' +
          '<span class="text-sm text-slate-500 dark:text-slate-400">' +
            escapeHtml(m.human) + '</span>' +
        '</div>' +
        '<div class="mt-1.5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">' +
          '<div class="h-full rounded-full ' + bar + '" style="width:' + width + '%"></div>' +
        '</div>' +
        '<p class="mt-1 text-xs text-slate-400 dark:text-slate-500">' + m.hours + ' hours</p>' +
      '</div>';
    });

    $box.html(html);
  }

  // ---------- Helpers ----------
  function since(iso) {
    return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  }

  function relative(seconds) {
    var secs = Math.max(0, Math.round(seconds || 0));
    if (secs < 60) return secs + 's';
    var mins = Math.floor(secs / 60);
    if (mins < 60) return mins + 'm';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ' + (mins % 60) + 'm';
    return Math.floor(hours / 24) + 'd';
  }

  function formatTime(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  }

  function escapeHtml(text) {
    if (!text) return '';
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function escapeAttr(text) {
    return escapeHtml(text).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ---------- Wiring ----------
  $('#gpu-refresh').on('click', function () { loadStatus(true); });
  $('#gpu-start').on('click', function () { confirmPower('start'); });
  $('#gpu-stop').on('click', function () { confirmPower('stop'); });

  $('#gpu-history-prev').on('click', function () {
    loadHistory(historyOffset - HISTORY_PAGE_SIZE);
  });
  $('#gpu-history-next').on('click', function () {
    loadHistory(historyOffset + HISTORY_PAGE_SIZE);
  });

  // Don't poll a tab nobody is looking at; catch up the moment it returns.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) { clearTimeout(pollTimer); } else { loadStatus(); }
  });

  loadStatus();
  loadHistory(0);
  loadUptime();
})(jQuery);
